import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SbIcon } from './icons/StatboxIcons';
import { useCommunityOptional } from '../contexts/CommunityContext';
import { isBasketballSport } from '../lib/communitySport';
import supabase from '../supabaseClient';
import { clearCommunityContext } from '../utils/communityAccessCache';
import { ANALYTICS_SIDEBAR_HIDDEN, COMPETITIONS_COMING_SOON, COMPETITIONS_SIDEBAR_HIDDEN, DISCOVERY_COMING_SOON } from '../lib/communityFeatureFlags';
import WorkspaceSwitchMenu, { useWorkspaceSwitch } from './portal/WorkspaceSwitchMenu';

/**
 * CommunitySidebar — left rail for the community portal.
 * Props:
 *   activeTab        — id of the currently active tab (matches item.id)
 *   onTabChange(id)  — fired when the user clicks a (non-locked) tab
 *   isOpen           — mobile drawer open state
 *   onClose          — close the mobile drawer
 *   collapsed        — desktop collapsed state
 *   onToggleCollapse — toggle desktop collapse (button hidden when omitted)
 */

const getInitials = (name) =>
  (name || 'M').split(' ').map((w) => w.charAt(0)).join('').toUpperCase().slice(0, 2);

const PRIMARY_ITEMS = [
  { id: 'dashboard',     label: 'Overview',      icon: 'overview' },
  { id: 'matches',       label: 'Matches',       icon: 'matches' },
  { id: 'growth',        label: 'Discovery',     icon: 'discovery' },
  { id: 'competitions',  label: 'Competitions',  icon: 'competitions' },
  { id: 'leaderboard',   label: 'Leaderboards',  icon: 'leaderboard' },
  { id: 'challenges',    label: 'Challenges',    icon: 'challenges' },
  { id: 'stats',         label: 'Analytics',     icon: 'analytics' },
  { id: 'video-library', label: 'Video Library', icon: 'library' },
];

const BOTTOM_ITEMS = [
  { id: 'management',       label: 'Management', icon: 'management' },
  { id: 'settings-config',  label: 'Settings',   icon: 'settings' },
];

const CommunitySidebar = ({
  activeTab,
  onTabChange,
  isOpen = false,
  onClose,
  collapsed = false,
  onToggleCollapse,
  headerOffset = 56,
}) => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const workspace = useWorkspaceSwitch('community');

  const community = useCommunityOptional();

  const orgData = {
    name: community?.communityName?.trim() || 'Community',
    logo: community?.communityLogo || null,
    sport: String(community?.communitySport || 'Football').trim() || 'Football',
    isLoading: !community?.contextReady,
  };

  const userName = community?.managerName || '';

  const isBasketball = isBasketballSport(orgData.sport);

  const handleLogout = async () => {
    setShowUserMenu(false);
    onClose?.();
    clearCommunityContext();
    await supabase.auth.signOut();
    navigate('/login');
  };

  useEffect(() => { setShowUserMenu(false); }, [collapsed]);
  useEffect(() => { if (!isOpen) setShowUserMenu(false); }, [isOpen]);

  const isComingSoon = (id) => {
    if (DISCOVERY_COMING_SOON && id === 'growth') return true;
    if (COMPETITIONS_COMING_SOON && id === 'competitions') return true;
    return false;
  };

  const renderItem = (item, isCollapsed) => {
    const comingSoon = isComingSoon(item.id);
    const disabled = comingSoon;
    const isActive = !disabled && activeTab === item.id;

    return (
      <div key={item.id} className="relative">
        <button
          type="button"
          data-onboarding={`nav-${item.id}`}
          onClick={() => {
            if (comingSoon) return;
            onTabChange(item.id);
          }}
          title={isCollapsed ? item.label : undefined}
          aria-label={comingSoon ? `${item.label} (coming soon)` : item.label}
          aria-disabled={disabled || undefined}
          className={`group w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-2.5'} ${isCollapsed ? 'p-2.5' : 'px-3 py-2'} rounded-lg transition-all duration-150 relative text-left ${
            isActive
              ? 'text-[#2F9E44]'
              : disabled
                ? 'text-[#C5C8CE] cursor-not-allowed'
                : 'text-[#5C6370] hover:text-[#111111] hover:bg-[#F7F7F8]'
          }`}
        >
          <SbIcon name={item.icon} size={18} className={`flex-shrink-0 ${isActive ? 'text-[#2F9E44]' : disabled ? 'text-[#C5C8CE]' : 'text-[#8A909A] group-hover:text-[#111111]'}`} />
          {!isCollapsed && (
            <>
              <span className="text-[13px] font-medium">{item.label}</span>
              {comingSoon && (
                <span className="ml-auto rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#0E1B4D] bg-[#EEF1F8] border border-[#D5DCEC]">
                  Soon
                </span>
              )}
            </>
          )}
        </button>
      </div>
    );
  };

  const Divider = ({ isCollapsed, label }) => (
    <div className={`${isCollapsed ? 'mx-2 my-4' : 'mx-1 mt-7 mb-4'}`}>
      <div className="border-t border-[#ECEDEF]" />
      {!isCollapsed && label && (
        <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 mt-4 px-3">{label}</p>
      )}
    </div>
  );

  const SidebarContent = ({ isCollapsed }) => (
    <div className="flex flex-col h-full">
      {/* Org block */}
      <div className={`flex-shrink-0 border-b border-[#ECEDEF] ${isCollapsed ? 'p-3' : 'px-4 py-5'}`}>
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`}>
          {orgData.logo ? (
            <div className={`${isCollapsed ? 'w-10 h-10' : 'w-9 h-9'} rounded-lg overflow-hidden ring-1 ring-[#E4E5E8] flex-shrink-0`}>
              <img src={orgData.logo} alt="" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className={`${isCollapsed ? 'w-10 h-10' : 'w-9 h-9'} rounded-full bg-[#D7F3E3] flex items-center justify-center flex-shrink-0`}>
              <span className="text-[#1B8A45] font-bold text-sm">{orgData.name.charAt(0).toUpperCase()}</span>
            </div>
          )}
          {!isCollapsed && (
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#111111] truncate">{orgData.name}</p>
              {orgData.sport ? (
                <p className="text-[11px] text-[#8A909A] inline-flex items-center gap-1">
                  {orgData.isLoading ? '...' : (
                    <>
                      <SbIcon name={isBasketball ? 'hoop' : 'ball'} size={12} />
                      {orgData.sport}
                    </>
                  )}
                </p>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {/* Primary nav — scrollable */}
      <nav className={`flex-1 overflow-y-auto ${isCollapsed ? 'p-2' : 'p-3'} space-y-0.5`}>
        {PRIMARY_ITEMS.filter(item => !(COMPETITIONS_SIDEBAR_HIDDEN && item.id === 'competitions') && !(ANALYTICS_SIDEBAR_HIDDEN && item.id === 'stats')).map(item => renderItem(item, isCollapsed))}
      </nav>

      {/* Bottom nav — pinned above account */}
      <div className={`flex-shrink-0 ${isCollapsed ? 'px-2 pb-1' : 'px-3 pb-1'} space-y-0.5`}>
        <Divider isCollapsed={isCollapsed} />
        {BOTTOM_ITEMS.map(item => renderItem(item, isCollapsed))}
      </div>

      {/* Account + collapse */}
      <div className="flex-shrink-0 border-t border-[#ECEDEF]">
        <div className={`${isCollapsed ? 'p-2' : 'p-3'} flex flex-col gap-2`}>
          <div className="relative">
            <button
              type="button"
              title={isCollapsed ? (userName || 'Account') : undefined}
              onClick={() => setShowUserMenu((v) => !v)}
              className={`w-full flex items-center rounded-lg transition-colors hover:bg-[#F7F7F8] ${
                isCollapsed ? 'justify-center p-2' : 'gap-2.5 px-2 py-2'
              }`}
            >
              <div className="w-8 h-8 rounded-full bg-[#D7F3E3] flex items-center justify-center flex-shrink-0">
                <span className="text-[#1B8A45] text-xs font-bold">{getInitials(userName)}</span>
              </div>
              {!isCollapsed && (
                <>
                  <span className="text-[13px] font-medium text-[#111111] truncate flex-1 text-left min-w-0">
                    {userName || 'Account'}
                  </span>
                  <SbIcon
                    name="chevronDown"
                    size={14}
                    className={`text-[#8A909A] flex-shrink-0 transition-transform duration-200 ${
                      showUserMenu ? 'rotate-180' : ''
                    }`}
                  />
                </>
              )}
            </button>

            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-[70]"
                  aria-hidden
                  onClick={() => setShowUserMenu(false)}
                />
                <div
                  className="absolute bottom-full left-0 mb-2 w-56 max-w-[calc(100vw-1rem)] bg-white rounded-xl shadow-xl border border-slate-200 z-[80] overflow-hidden text-left"
                >
                  <div className="px-4 py-3 border-b border-slate-100">
                    <p className="text-sm font-semibold text-slate-900">{userName || '—'}</p>
                    <p className="text-xs text-slate-500 truncate">{orgData.name}</p>
                  </div>
                  <WorkspaceSwitchMenu tone="community" {...workspace} />
                  <div className="py-1">
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-[#111111] hover:bg-[#F7F7F8] transition-colors"
                    >
                      <SbIcon name="logout" size={16} /> Log out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {onToggleCollapse && (
            <div className="hidden lg:block pt-0.5">
              <button
                type="button"
                onClick={onToggleCollapse}
                className="w-full flex items-center justify-center p-2 rounded-lg text-[#8A909A] hover:text-[#111111] hover:bg-[#F7F7F8] transition-colors"
                aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <SbIcon name={isCollapsed ? 'chevronRight' : 'chevronLeft'} size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile sidebar */}
      <div
        className={`lg:hidden fixed left-0 w-[220px] bg-[#F7F8FA] border-r border-[#E7E7EA] text-[#111111] z-40 flex flex-col font-jakarta transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ top: headerOffset, height: `calc(100vh - ${headerOffset}px)` }}
      >
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-[#F2F3F5] transition-colors z-20" aria-label="Close menu">
          <SbIcon name="close" size={16} className="text-[#111111]" />
        </button>
        <SidebarContent isCollapsed={false} />
      </div>

      {/* Desktop sidebar */}
      <div
        className={`hidden lg:flex bg-[#F7F8FA] border-r border-[#E7E7EA] fixed left-0 text-[#111111] z-30 flex-col font-jakarta transition-all duration-200 ${collapsed ? 'w-[72px]' : 'w-[200px]'}`}
        style={{ top: headerOffset, height: `calc(100vh - ${headerOffset}px)` }}
      >
        <SidebarContent isCollapsed={collapsed} />
      </div>
    </>
  );
};

export default CommunitySidebar;
